import React, { useState, useEffect, useRef } from "react";
import { getFirestore, doc, updateDoc, onSnapshot, getDoc, collection, query, where, getDocs, setDoc, limit } from "firebase/firestore";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { sendPasswordResetEmail } from "firebase/auth";
import app, { studentAuth } from "../../firebase";
import { readNewStudentSession, writeNewStudentSession } from "../../auth/roleSession";
import { useNavigate, useLocation } from "react-router-dom";
import { useSession } from "../../context/SessionContext";
import CourseLineup from "./CourseLineup";
import StudentFinance from "./StudentFinance";
import FinalEnrollment from "./FinalEnrollment";
import PreRegistrationProfileInfo from "./PreRegistration";
import {
  calendarDateToLocalDate,
  formatCalendarDate,
  isCalendarDateInRange,
  toCalendarDateKey,
  useCalendarClock
} from "../../utils/announcementDates";

const SIDEBAR_BUTTONS = [
  { key: "home", label: "Home" },
  { key: "assessment", label: "Assessment" },
  { key: "pre-registration", label: "Pre-Registration" },
  { key: "course-lineup", label: "Course Line-up" },
  { key: "finances", label: "Finances" },
  { key: "final-enrollment", label: "Finalization of Enrollment" }, 
];

function getLatestTermSchedule(studentData) {
  const termSchedules = studentData?.termSchedules || {};
  const termKeys = Object.keys(termSchedules);
  if (termKeys.length === 0) return studentData?.schedules || [];
  return termSchedules[termKeys[termKeys.length - 1]] || [];
}
const localStorage = window.sessionStorage;

export default function StudentDashboard({ student: studentProp, onLogout }) {
  // Announcement and enrollment window
  const todayKey = useCalendarClock();
  const navigate = useNavigate();
  const { currentUser, logout, loading } = useSession();
  const isInitialRef = useRef(true); // Track if this is first render
  const previousUserRef = useRef(currentUser); // Track previous currentUser to detect actual logout
  
  // Fallback to localStorage if student prop not provided
  const [student, setStudent] = useState(() => {
    if (studentProp) return studentProp;
    // Try to recover from localStorage
    const storedStudent = readNewStudentSession();
    const stored = storedStudent ? JSON.stringify(storedStudent) : null;
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error("[StudentDashboard] Failed to parse stored student:", e);
      }
    }
    return null;
  });
  
  const [announcement, setAnnouncement] = useState("");
  
  // 1st Semester
  const [firstSemStart, setFirstSemStart] = useState("");
  const [firstSemEnd, setFirstSemEnd] = useState("");
  const [firstSemEnrollmentStart, setFirstSemEnrollmentStart] = useState("");
  const [firstSemEnrollmentEnd, setFirstSemEnrollmentEnd] = useState("");
  const [firstSemAppointmentStart, setFirstSemAppointmentStart] = useState("");
  const [firstSemAppointmentEnd, setFirstSemAppointmentEnd] = useState("");
  
  // 2nd Semester
  const [secondSemStart, setSecondSemStart] = useState("");
  const [secondSemEnd, setSecondSemEnd] = useState("");
  const [secondSemEnrollmentStart, setSecondSemEnrollmentStart] = useState("");
  const [secondSemEnrollmentEnd, setSecondSemEnrollmentEnd] = useState("");
  const [secondSemAppointmentStart, setSecondSemAppointmentStart] = useState("");
  const [secondSemAppointmentEnd, setSecondSemAppointmentEnd] = useState("");

  // Redirect to /signin when user logs out (only if they were previously logged in)
  useEffect(() => {
    // Skip if Firebase is still loading - null might just be auth initializing
    if (loading) {
      return;
    }

    // Skip the first render - we want to detect CHANGES, not initial state
    if (isInitialRef.current) {
      isInitialRef.current = false;
      previousUserRef.current = currentUser;
      console.log("[StudentDashboard] First render, skipping logout check. currentUser:", currentUser ? "set" : "null", "loading:", loading);
      return;
    }

    // Now detect if user actually logged out (went from non-null to null)
    const wasLoggedIn = previousUserRef.current !== null;
    const isNowNull = currentUser === null;
    const hasStudentFlag = localStorage.getItem("studentPortalLogin") === "true";

    if (wasLoggedIn && isNowNull && hasStudentFlag) {
      console.log("[StudentDashboard] Detected logout: user was logged in, now null, flag still set");
      localStorage.removeItem("studentPortalLogin");
      localStorage.removeItem("currentStudentSession");
      navigate("/signin", { replace: true });
    }

    // Update the ref
    previousUserRef.current = currentUser;
  }, [currentUser, navigate, loading]);

  // Listen for logoutEvent from other tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "logoutEvent") {
        console.log("[StudentDashboard] Detected logoutEvent from another tab");
        localStorage.removeItem("studentPortalLogin");
        localStorage.removeItem("currentStudentSession");
        navigate("/signin", { replace: true });
      }
    };
    
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [navigate]);

  useEffect(() => {
    const db = getFirestore(app);
    const announcementRef = doc(db, "systemSettings", "announcement");
    const unsub = onSnapshot(announcementRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setAnnouncement(data.announcement || "");
        
        // 1st Semester
        setFirstSemStart(data.firstSemStart || "");
        setFirstSemEnd(data.firstSemEnd || "");
        setFirstSemEnrollmentStart(data.firstSemEnrollmentStart || "");
        setFirstSemEnrollmentEnd(data.firstSemEnrollmentEnd || "");
        setFirstSemAppointmentStart(data.firstSemAppointmentStart || "");
        setFirstSemAppointmentEnd(data.firstSemAppointmentEnd || "");
        
        // 2nd Semester
        setSecondSemStart(data.secondSemStart || "");
        setSecondSemEnd(data.secondSemEnd || "");
        setSecondSemEnrollmentStart(data.secondSemEnrollmentStart || "");
        setSecondSemEnrollmentEnd(data.secondSemEnrollmentEnd || "");
        setSecondSemAppointmentStart(data.secondSemAppointmentStart || "");
        setSecondSemAppointmentEnd(data.secondSemAppointmentEnd || "");
      }
    });
    return () => unsub();
  }, []);

  // Determine current semester (1st or 2nd) based on actual semester dates
  const getCurrentSemesterDates = () => {
    if (isCalendarDateInRange(firstSemStart, firstSemEnd, todayKey)) {
      return {
        enrollmentStart: firstSemEnrollmentStart,
        enrollmentEnd: firstSemEnrollmentEnd,
        appointmentStart: firstSemAppointmentStart,
        appointmentEnd: firstSemAppointmentEnd,
        enrollmentStatus: isCalendarDateInRange(firstSemEnrollmentStart, firstSemEnrollmentEnd, todayKey) ? "OPEN" : "CLOSED",
        appointmentStatus: isCalendarDateInRange(firstSemAppointmentStart, firstSemAppointmentEnd, todayKey) ? "OPEN" : "CLOSED"
      };
    }

    if (isCalendarDateInRange(secondSemStart, secondSemEnd, todayKey)) {
      return {
        enrollmentStart: secondSemEnrollmentStart,
        enrollmentEnd: secondSemEnrollmentEnd,
        appointmentStart: secondSemAppointmentStart,
        appointmentEnd: secondSemAppointmentEnd,
        enrollmentStatus: isCalendarDateInRange(secondSemEnrollmentStart, secondSemEnrollmentEnd, todayKey) ? "OPEN" : "CLOSED",
        appointmentStatus: isCalendarDateInRange(secondSemAppointmentStart, secondSemAppointmentEnd, todayKey) ? "OPEN" : "CLOSED"
      };
    }
    
    // Fallback: if no semester matches, use month-based logic
    if (Number(todayKey.slice(5, 7)) <= 6) {
      return {
        enrollmentStart: firstSemEnrollmentStart,
        enrollmentEnd: firstSemEnrollmentEnd,
        appointmentStart: firstSemAppointmentStart,
        appointmentEnd: firstSemAppointmentEnd,
        enrollmentStatus: isCalendarDateInRange(firstSemEnrollmentStart, firstSemEnrollmentEnd, todayKey) ? "OPEN" : "CLOSED",
        appointmentStatus: isCalendarDateInRange(firstSemAppointmentStart, firstSemAppointmentEnd, todayKey) ? "OPEN" : "CLOSED"
      };
    } else {
      return {
        enrollmentStart: secondSemEnrollmentStart,
        enrollmentEnd: secondSemEnrollmentEnd,
        appointmentStart: secondSemAppointmentStart,
        appointmentEnd: secondSemAppointmentEnd,
        enrollmentStatus: isCalendarDateInRange(secondSemEnrollmentStart, secondSemEnrollmentEnd, todayKey) ? "OPEN" : "CLOSED",
        appointmentStatus: isCalendarDateInRange(secondSemAppointmentStart, secondSemAppointmentEnd, todayKey) ? "OPEN" : "CLOSED"
      };
    }
  };

  const currentSemesterDates = getCurrentSemesterDates();
  const enrollmentOpenFlag = currentSemesterDates.enrollmentStatus === "OPEN";
  const appointmentOpenFlag = currentSemesterDates.appointmentStatus === "OPEN";

  // Helper function to get school year from semester start date
  const getSchoolYear = (semesterStartDate) => {
    if (!semesterStartDate) return "AY XXXX-XXXX";
    const year = Number(toCalendarDateKey(semesterStartDate).slice(0, 4));
    if (!year) return "AY XXXX-XXXX";
    return `AY ${year}-${year + 1}`;
  };

  // Helper function to determine which semester the current date falls into
  const getCurrentSemesterInfo = () => {
    if (isCalendarDateInRange(firstSemStart, firstSemEnd, todayKey)) {
      return {
        semester: '1st',
        schoolYear: getSchoolYear(firstSemStart),
        isInSemester: true,
        enrollmentStart: firstSemEnrollmentStart,
        enrollmentEnd: firstSemEnrollmentEnd,
        nextSemesterEnrollmentStart: secondSemEnrollmentStart,
        nextSemesterEnrollmentEnd: secondSemEnrollmentEnd
      };
    }
    
    if (isCalendarDateInRange(secondSemStart, secondSemEnd, todayKey)) {
      return {
        semester: '2nd',
        schoolYear: getSchoolYear(secondSemStart),
        isInSemester: true,
        enrollmentStart: secondSemEnrollmentStart,
        enrollmentEnd: secondSemEnrollmentEnd,
        nextSemesterEnrollmentStart: firstSemEnrollmentStart, // Next cycle
        nextSemesterEnrollmentEnd: firstSemEnrollmentEnd
      };
    }
    
    // If not in any semester range, return null
    return {
      semester: null,
      schoolYear: null,
      isInSemester: false
    };
  };

  const semesterInfo = getCurrentSemesterInfo();

  // Helper function to get enrollment window display text
  const getEnrollmentWindowText = (enrollmentStart, enrollmentEnd) => {
    if (!enrollmentStart || !enrollmentEnd) return "TBD";
    const startMonth = formatCalendarDate(enrollmentStart, { month: 'short', day: 'numeric', year: undefined });
    const endMonth = formatCalendarDate(enrollmentEnd, { month: 'short', day: 'numeric', year: undefined });
    return `${startMonth} - ${endMonth}`;
  };

  // Generate enrollment status message based on semester and enrollment flag
  const getEnrollmentStatusMessage = () => {
    if (!semesterInfo.isInSemester) {
      return "No active semester period.";
    }

    const { semester, schoolYear, enrollmentStart, enrollmentEnd, nextSemesterEnrollmentStart, nextSemesterEnrollmentEnd } = semesterInfo;
    
    if (enrollmentOpenFlag) {
      // Enrollment is OPEN
      const windowText = getEnrollmentWindowText(enrollmentStart, enrollmentEnd);
      return `Enrollment for ${schoolYear} ${semester} Semester is now OPEN. Enrollment period: ${windowText}`;
    } else {
      // Enrollment is CLOSED
      const nextWindowText = getEnrollmentWindowText(nextSemesterEnrollmentStart, nextSemesterEnrollmentEnd);
      const nextSem = semester === '1st' ? '2nd' : '1st';
      return `Enrollment for ${schoolYear} ${semester} Semester is now CLOSED. Enrollment for ${schoolYear} ${nextSem} Semester will open: ${nextWindowText}`;
    }
  };
  const location = useLocation();
  const initialActive = location.state?.active || "home";
  const [active, setActive] = useState(initialActive);
  // Track local schedules state to enable Finances immediately after SUBMIT
  const [localSchedules, setLocalSchedules] = useState(getLatestTermSchedule(student));
  const [lineupSubmitted, setLineupSubmitted] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [assessmentSchedule, setAssessmentSchedule] = useState(null); // { date, time }
  const [assessmentStatus, setAssessmentStatus] = useState(student.appointment?.status);
  const [canPreRegister, setCanPreRegister] = useState(
    String(student.appointment?.status || "").trim().toLowerCase() === "approved"
  );
  const [preRegistrationStatus, setPreRegistrationStatus] = useState(student.preRegistrationStatus);
  const [hasCor, setHasCor] = useState(false);
  const [canFinalize, setCanFinalize] = useState(false);

  // NEW: track registrationStatus (persisted in Firestore) so page reload still knows if line-up was submitted
  const [registrationStatus, setRegistrationStatus] = useState(student.registrationStatus || null);

  // NEW: add missing state variables for FinalEnrollment
  const [localStudent, setLocalStudent] = useState(student);
  const [miscFees, setMiscFees] = useState([]);
  const [cashDiscount, setCashDiscount] = useState(0);
  const [cashWithDiscountDate, setCashWithDiscountDate] = useState("");
  const [cashWithoutDiscountDate, setCashWithoutDiscountDate] = useState("");
  const [installmentDates, setInstallmentDates] = useState([]);

  // Real-time Firestore listener for student document (listen by idNumber field)
  useEffect(() => {
    if (!student?.idNumber) {
      console.log('Student idNumber not available yet, skipping listener');
      return;
    }
    const db = getFirestore(app);
    console.log('Setting up query listener for student idNumber:', student.idNumber); // DEBUG

    const q = query(collection(db, "students"), where("idNumber", "==", student.idNumber), limit(1));
    const unsubscribe = onSnapshot(q, (snap) => {
      console.log('Query listener fired, empty:', snap.empty); // DEBUG
      if (snap.empty) {
        // No matching document
        setAssessmentSchedule(null);
        setAssessmentStatus(undefined);
        setCanPreRegister(false);
        setPreRegistrationStatus(undefined);
        setLocalSchedules([]);
        return;
      }
      const docSnap = snap.docs[0];
      const data = docSnap.data();
      console.log('Student doc from query:', docSnap.id, data); // DEBUG

      // Keep a local copy of the full student doc so UI updates (profile picture etc.) reflect immediately
      setLocalStudent({ id: docSnap.id, ...data });

      // Update assessmentSchedule
      if (data.appointment && data.appointment.date && data.appointment.time) {
        setAssessmentSchedule({ date: data.appointment.date, time: data.appointment.time });
      } else {
        setAssessmentSchedule(null);
      }

      const status = String(data.appointment?.status || "").trim().toLowerCase();
      setAssessmentStatus(data.appointment?.status);
      setCanPreRegister(status === "approved");
      setPreRegistrationStatus(data.preRegistrationStatus);
      setLocalSchedules(getLatestTermSchedule(data));

      // NEW: persist registrationStatus from Firestore and derive lineupSubmitted
      setRegistrationStatus(data.registrationStatus || null);
      // if backend stored registrationStatus === "submitted", reflect that immediately
      if (String(data.registrationStatus || "").toLowerCase() === "submitted") {
        setLineupSubmitted(true);
      }

      // new: normalize detection of COR presence
      const hasCor = Boolean(
        data.cor?.corPdfUrl ||           // ← NEW: Check nested cor object
        data.cor?.corPdfGeneratedAt ||   // ← NEW: COR exists even without PDF
        data.corPdfUrl ||                // Old: root level
        data.corUrl ||                   // Old: root level
        data.requirements?.corUploaded ||
        (Array.isArray(data.requirements) && data.requirements.includes('cor'))
      );
      setHasCor(hasCor);
      console.log("COR detection:", { hasCor, corData: data.cor }); // ← DEBUG
      // compute finalization readiness
      const canFinalize = (
        String(data.appointment?.status || '').toLowerCase() === 'approved' &&
        (String(data.preRegistrationStatus || '').toLowerCase() === 'submitted' || data.preRegistrationStatus === 'complete') &&
        hasCor &&
        (String(data.financeStatus || '').toLowerCase() === 'paid' || data.financeStatus === 'fully paid')
      );
      setCanFinalize(Boolean(canFinalize));
    });

    return () => unsubscribe();
  }, [student?.idNumber]);  // CHANGE THIS dependency too
  const formatTime = (time) => {
    if (time === "am") return "AM 9:00-12:00";
    if (time === "pm") return "PM 1:30-4:30";
    return "-";
  };

  // Add missing formatDate function
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };
  // Prefer localStudent profilePicUrl when available so header updates immediately after edit
  const profilePicUrl = localStudent?.profilePicUrl || student?.profilePicUrl || "https://ui-avatars.com/api/?name=" + encodeURIComponent((localStudent?.firstName || student?.firstName || '') + ' ' + (localStudent?.lastName || student?.lastName || ''));
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [changePassOpen, setChangePassOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [editFile, setEditFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [editMsg, setEditMsg] = useState("");
  const [changePassMsg, setChangePassMsg] = useState("");
  const [changingPass, setChangingPass] = useState(false);

  const [editInfoOpen, setEditInfoOpen] = useState(false);
  const [editInfoSaving, setEditInfoSaving] = useState(false);
  const [editInfoError, setEditInfoError] = useState("");
  const [editInfoForm, setEditInfoForm] = useState({
    category: "",
    program: "",
    firstName: "",
    middleName: "",
    middleNameNA: false,
    lastName: "",
    gender: "",
    dob: "",
    mobile: "",
    email: "",
    pictureUrl: "",
    birthCertUrl: "",
    schoolIdUrl: "",
    gradesUrl: "",
    pictureHardcopy: false,
    birthCertHardcopy: false,
    schoolIdHardcopy: false,
    gradesHardcopy: false,
    preRegistration: {
      entryLevel: "",
      semester: "",
      nationality: "",
      religion: "",
      civilStatus: "",
      birthPlace: "",
      present: { house: "", barangay: "", city: "", province: "", postal: "", country: "" },
      permanent: { house: "", barangay: "", city: "", province: "", postal: "", country: "" },
      sameAsPresent: false,
      elementary: { school: "", address: "", year: "" },
      junior: { school: "", address: "", year: "" },
      senior: { school: "", address: "", year: "", strand: "" },
      parentName: "",
      relationship: "",
      occupation: "",
      familyAddress: "",
      familyMobile: "",
    }
  });

  const [editInfoFiles, setEditInfoFiles] = useState({
    picture: null,
    birthCert: null,
    schoolId: null,
    grades: null,
  });
  const [editInfoFieldErrors, setEditInfoFieldErrors] = useState({});

  const isValidEmail = (val) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(val);
  const isValidMobile = (val) => /^09\d{9}$/.test(val);
  const isValidFile = (file) => {
    if (!file) return false;
    const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
    return allowedTypes.includes(file.type) && file.size <= 5 * 1024 * 1024;
  };
  const getFileErrorMessage = (file) => {
    if (!file) return "";
    const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
    const maxSize = 5 * 1024 * 1024;
    if (!allowedTypes.includes(file.type)) {
      return "Only JPG, PNG, or PDF files are allowed.";
    }
    if (file.size > maxSize) {
      return `File size must be 5MB or below (${(file.size / 1024 / 1024).toFixed(2)}MB).`;
    }
    return "";
  };

  // Validation helpers (match PreRegistration rules)
  const currentYear = new Date().getFullYear();
  const isAlphaSpace = (val) => typeof val === 'string' && /^[A-Za-z\s]+$/.test(val);
  const isValidYear = (val) => typeof val === 'string' && /^\d{4}$/.test(val) && Number(val) >= 1920 && Number(val) <= currentYear;
  const processAlphaText = (val, max = 50) => {
    if (typeof val !== 'string') return val;
    return val.replace(/[^A-Za-z\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, max);
  };

  // Helper to map a field path to possible error keys used in validation
  const getEditFieldError = (path) => {
    if (!editInfoFieldErrors) return "";
    const keys = path.split('.');
    const last = keys[keys.length - 1];
    const simple = last;
    const pathKey = path;
    let combined = '';
    if (keys.length >= 2) {
      const parent = keys[keys.length - 2];
      combined = `${parent}${last.charAt(0).toUpperCase()}${last.slice(1)}`;
    }
    return editInfoFieldErrors[simple] || editInfoFieldErrors[pathKey] || editInfoFieldErrors[combined] || "";
  };

  const validateEditInfoForm = () => {
    const errors = {};
    const value = (val) => (typeof val === 'string' ? val.trim() : '');
    if (!value(editInfoForm.category)) errors.category = 'Category is required.';
    if (!value(editInfoForm.program)) errors.program = 'Program is required.';
    if (!value(editInfoForm.firstName)) errors.firstName = 'First name is required.';
    if (!value(editInfoForm.lastName)) errors.lastName = 'Last name is required.';
    if (!value(editInfoForm.gender)) errors.gender = 'Gender is required.';
    if (!value(editInfoForm.dob)) errors.dob = 'Date of birth is required.';
    if (!value(editInfoForm.mobile)) {
      errors.mobile = 'Mobile number is required.';
    } else if (!isValidMobile(value(editInfoForm.mobile))) {
      errors.mobile = 'Enter a valid mobile number starting with 09 and containing 11 digits.';
    }
    if (!value(editInfoForm.email)) {
      errors.email = 'Email is required.';
    } else if (!isValidEmail(value(editInfoForm.email))) {
      errors.email = 'Enter a valid email address.';
    }

    if (!value(editInfoForm.preRegistration.entryLevel)) errors.entryLevel = 'Entry Level is required.';
    if (!value(editInfoForm.preRegistration.semester)) errors.semester = 'Semester is required.';
    if (!value(editInfoForm.preRegistration.nationality)) errors.nationality = 'Nationality is required.';
    if (!value(editInfoForm.preRegistration.religion)) errors.religion = 'Religion is required.';
    if (!value(editInfoForm.preRegistration.civilStatus)) errors.civilStatus = 'Civil status is required.';
    if (!value(editInfoForm.preRegistration.birthPlace)) errors.birthPlace = 'Birth place is required.';

    // alpha-only checks for location and identity fields
    if (value(editInfoForm.preRegistration.nationality) && !isAlphaSpace(editInfoForm.preRegistration.nationality)) {
      errors.nationality = 'Invalid characters - letters only';
    }
    if (value(editInfoForm.preRegistration.religion) && !isAlphaSpace(editInfoForm.preRegistration.religion)) {
      errors.religion = 'Invalid characters - letters only';
    }
    if (value(editInfoForm.preRegistration.birthPlace) && !isAlphaSpace(editInfoForm.preRegistration.birthPlace)) {
      errors.birthPlace = 'Invalid characters - letters only';
    }

    if (!value(editInfoForm.preRegistration.present.house)) errors.presentHouse = 'Present house/street is required.';
    if (!value(editInfoForm.preRegistration.present.barangay)) errors.presentBarangay = 'Present barangay is required.';
    if (!value(editInfoForm.preRegistration.present.city)) errors.presentCity = 'Present city is required.';
    if (!value(editInfoForm.preRegistration.present.province)) errors.presentProvince = 'Present province is required.';
    if (!value(editInfoForm.preRegistration.present.postal)) errors.presentPostal = 'Present postal code is required.';
    if (!value(editInfoForm.preRegistration.present.country)) errors.presentCountry = 'Present country is required.';

    // present address alpha checks
    if (value(editInfoForm.preRegistration.present.city) && !isAlphaSpace(editInfoForm.preRegistration.present.city)) {
      errors.presentCity = 'Invalid characters - letters only';
    }
    if (value(editInfoForm.preRegistration.present.province) && !isAlphaSpace(editInfoForm.preRegistration.present.province)) {
      errors.presentProvince = 'Invalid characters - letters only';
    }
    if (value(editInfoForm.preRegistration.present.country) && !isAlphaSpace(editInfoForm.preRegistration.present.country)) {
      errors.presentCountry = 'Invalid characters - letters only';
    }

    if (!editInfoForm.preRegistration.sameAsPresent) {
      if (!value(editInfoForm.preRegistration.permanent.house)) errors.permanentHouse = 'Permanent house/street is required.';
      if (!value(editInfoForm.preRegistration.permanent.barangay)) errors.permanentBarangay = 'Permanent barangay is required.';
      if (!value(editInfoForm.preRegistration.permanent.city)) errors.permanentCity = 'Permanent city is required.';
      if (!value(editInfoForm.preRegistration.permanent.province)) errors.permanentProvince = 'Permanent province is required.';
      if (!value(editInfoForm.preRegistration.permanent.postal)) errors.permanentPostal = 'Permanent postal code is required.';
      if (!value(editInfoForm.preRegistration.permanent.country)) errors.permanentCountry = 'Permanent country is required.';
      // permanent address alpha checks
      if (value(editInfoForm.preRegistration.permanent.city) && !isAlphaSpace(editInfoForm.preRegistration.permanent.city)) {
        errors.permanentCity = 'Invalid characters - letters only';
      }
      if (value(editInfoForm.preRegistration.permanent.province) && !isAlphaSpace(editInfoForm.preRegistration.permanent.province)) {
        errors.permanentProvince = 'Invalid characters - letters only';
      }
      if (value(editInfoForm.preRegistration.permanent.country) && !isAlphaSpace(editInfoForm.preRegistration.permanent.country)) {
        errors.permanentCountry = 'Invalid characters - letters only';
      }
    }

    if (!value(editInfoForm.preRegistration.familyAddress)) errors.familyAddress = 'Family address is required.';
    if (!value(editInfoForm.preRegistration.familyMobile)) {
      errors.familyMobile = 'Family mobile number is required.';
    } else if (!/^[0-9]{10,11}$/.test(editInfoForm.preRegistration.familyMobile.replace(/\D/g, ''))) {
      errors.familyMobile = 'Enter a valid family mobile number with 10 or 11 digits.';
    }

    // education and parent fields validation
    if (value(editInfoForm.preRegistration.elementary.school) && !isAlphaSpace(editInfoForm.preRegistration.elementary.school)) {
      errors.elementarySchool = 'Invalid characters - letters only';
    }
    if (value(editInfoForm.preRegistration.junior.school) && !isAlphaSpace(editInfoForm.preRegistration.junior.school)) {
      errors.juniorSchool = 'Invalid characters - letters only';
    }
    if (value(editInfoForm.preRegistration.senior.school) && !isAlphaSpace(editInfoForm.preRegistration.senior.school)) {
      errors.seniorSchool = 'Invalid characters - letters only';
    }
    if (value(editInfoForm.preRegistration.parentName) && !isAlphaSpace(editInfoForm.preRegistration.parentName)) {
      errors.parentName = 'Invalid characters - letters only';
    }
    if (value(editInfoForm.preRegistration.occupation) && !isAlphaSpace(editInfoForm.preRegistration.occupation)) {
      errors.occupation = 'Invalid characters - letters only';
    }
    if (value(editInfoForm.preRegistration.senior.strand) && !isAlphaSpace(editInfoForm.preRegistration.senior.strand)) {
      errors.seniorStrand = 'Invalid characters - letters only';
    }

    // year validations
    if (value(editInfoForm.preRegistration.elementary.year) && !isValidYear(String(editInfoForm.preRegistration.elementary.year))) {
      errors.elementaryYear = 'Enter a valid year';
    }
    if (value(editInfoForm.preRegistration.junior.year) && !isValidYear(String(editInfoForm.preRegistration.junior.year))) {
      errors.juniorYear = 'Enter a valid year';
    }
    if (value(editInfoForm.preRegistration.senior.year) && !isValidYear(String(editInfoForm.preRegistration.senior.year))) {
      errors.seniorYear = 'Enter a valid year';
    }

    const attachments = [
      { field: 'picture', label: '2x2 picture' },
      { field: 'birthCert', label: 'Birth/Marriage Certificate' },
      { field: 'schoolId', label: 'School ID/Government ID' },
      { field: 'grades', label: 'Copy of Grades' },
    ];

    attachments.forEach(({ field, label }) => {
      const url = editInfoForm[`${field}Url`];
      const hardcopy = editInfoForm[`${field}Hardcopy`];
      const file = editInfoFiles[field];
      if (!url && !hardcopy && !file) {
        errors[field] = `Provide ${label} or choose hard copy later.`;
      } else if (file) {
        const fileError = getFileErrorMessage(file);
        if (fileError) errors[field] = fileError;
      }
    });

    setEditInfoFieldErrors(errors);
    return errors;
  };

  const handleEditInfoFile = (field, file) => {
    if (!file) return;
    const error = getFileErrorMessage(file);
    setEditInfoFieldErrors((prev) => ({ ...prev, [field]: error }));
    setEditInfoFiles((prev) => ({ ...prev, [field]: error ? null : file }));
  };

  const handleToggleHardcopy = (field, checked) => {
    handleEditInfoField(`${field}Hardcopy`, checked);
    if (checked) {
      setEditInfoFiles((prev) => ({ ...prev, [field]: null }));
    }
  };

  const openEditInfoModal = () => {
    const source = localStudent || student || {};
    const pre = source.preRegistration || {};
    setEditInfoForm({
      category: source.category || "",
      program: source.program || "",
      firstName: source.firstName || "",
      middleName: source.middleName || "",
      middleNameNA: !!source.middleNameNA,
      lastName: source.lastName || "",
      gender: source.gender || "",
      dob: source.dob || "",
      mobile: source.mobile || "",
      email: source.email || "",
      pictureUrl: source.picture || "",
      birthCertUrl: source.birthCert || "",
      schoolIdUrl: source.schoolId || "",
      gradesUrl: source.grades || "",
      pictureHardcopy: !!source.pictureHardcopy,
      birthCertHardcopy: !!source.birthCertHardcopy,
      schoolIdHardcopy: !!source.schoolIdHardcopy,
      gradesHardcopy: !!source.gradesHardcopy,
      preRegistration: {
        entryLevel: pre.entryLevel || "",
        semester: pre.semester || "",
        nationality: pre.nationality || "",
        religion: pre.religion || "",
        civilStatus: pre.civilStatus || "",
        birthPlace: pre.birthPlace || "",
        present: {
          house: pre.present?.house || "",
          barangay: pre.present?.barangay || "",
          city: pre.present?.city || "",
          province: pre.present?.province || "",
          postal: pre.present?.postal || "",
          country: pre.present?.country || "",
        },
        permanent: {
          house: pre.permanent?.house || "",
          barangay: pre.permanent?.barangay || "",
          city: pre.permanent?.city || "",
          province: pre.permanent?.province || "",
          postal: pre.permanent?.postal || "",
          country: pre.permanent?.country || "",
        },
        sameAsPresent: !!pre.sameAsPresent,
        elementary: {
          school: pre.elementary?.school || "",
          address: pre.elementary?.address || "",
          year: pre.elementary?.year || "",
        },
        junior: {
          school: pre.junior?.school || "",
          address: pre.junior?.address || "",
          year: pre.junior?.year || "",
        },
        senior: {
          school: pre.senior?.school || "",
          address: pre.senior?.address || "",
          year: pre.senior?.year || "",
          strand: pre.senior?.strand || "",
        },
        parentName: pre.parentName || "",
        relationship: pre.relationship || "",
        occupation: pre.occupation || "",
        familyAddress: pre.familyAddress || "",
        familyMobile: pre.familyMobile || "",
      }
    });
    setEditInfoFiles({ picture: null, birthCert: null, schoolId: null, grades: null });
    setEditInfoFieldErrors({});
    setEditInfoError("");
    setEditInfoOpen(true);
  };

  const handleEditInfoField = (path, value) => {
    // sanitize certain fields
    const alphaPaths = new Set([
      'preRegistration.nationality', 'preRegistration.religion', 'preRegistration.birthPlace',
      'preRegistration.present.city', 'preRegistration.present.province', 'preRegistration.present.country',
      'preRegistration.permanent.city', 'preRegistration.permanent.province', 'preRegistration.permanent.country',
      'preRegistration.elementary.school', 'preRegistration.junior.school', 'preRegistration.senior.school',
      'preRegistration.senior.strand', 'preRegistration.parentName', 'preRegistration.occupation'
    ]);
    if (alphaPaths.has(path)) {
      value = processAlphaText(String(value || ''));
    }
    if (path.endsWith('.year')) {
      // only digits, max 4
      value = String(value || '').replace(/\D/g, '').slice(0, 4);
    }

    setEditInfoForm((prev) => {
      const next = { ...prev };
      const keys = path.split('.');
      let target = next;
      for (let i = 0; i < keys.length - 1; i += 1) {
        const key = keys[i];
        target[key] = { ...target[key] };
        target = target[key];
      }
      target[keys[keys.length - 1]] = value;
      return next;
    });
    const keys = path.split('.');
    const key = keys.slice(-1)[0];
    const combinedKey = keys.length >= 2 ? `${keys[keys.length - 2]}${key.charAt(0).toUpperCase()}${key.slice(1)}` : null;
    setEditInfoFieldErrors((prev) => ({ ...prev, [key]: "", [path]: "", ...(combinedKey ? { [combinedKey]: "" } : {}) }));
  };

  const resolveStudentDocRef = async (db) => {
    const docId = localStudent?.id || student?.id;
    if (docId) {
      const directRef = doc(db, 'students', docId);
      const directSnap = await getDoc(directRef);
      if (directSnap.exists()) return directRef;
    }
    if (student?.idNumber) {
      const q = query(collection(db, 'students'), where('idNumber', '==', student.idNumber), limit(1));
      const snap = await getDocs(q);
      if (!snap.empty) return snap.docs[0].ref;
    }
    return null;
  };

  const handleSaveEditInfo = async (e) => {
    e.preventDefault();
    setEditInfoError("");
    setEditInfoSaving(true);
    try {
      const fieldErrors = validateEditInfoForm();
      if (Object.keys(fieldErrors).length > 0) {
        setEditInfoError('Please fix the highlighted fields before saving.');
        setEditInfoSaving(false);
        return;
      }

      const db = getFirestore(app);
      const studentRef = await resolveStudentDocRef(db);
      if (!studentRef) {
        throw new Error('Student record not found.');
      }

      const storage = getStorage(app);
      const studentStorageKey = studentRef.id || student?.idNumber || 'student';
      const attachmentFields = ['picture', 'birthCert', 'schoolId', 'grades'];
      const attachmentUpdates = {};
      for (const field of attachmentFields) {
        const file = editInfoFiles[field];
        if (file) {
          const sRef = storageRef(storage, `students_uploads/${studentStorageKey}/${field}_${Date.now()}_${file.name}`);
          await uploadBytes(sRef, file);
          attachmentUpdates[`${field}Url`] = await getDownloadURL(sRef);
        }
      }

      const updateData = {
        category: editInfoForm.category,
        program: editInfoForm.program,
        firstName: editInfoForm.firstName,
        middleName: editInfoForm.middleName,
        middleNameNA: !!editInfoForm.middleNameNA,
        lastName: editInfoForm.lastName,
        gender: editInfoForm.gender,
        dob: editInfoForm.dob,
        mobile: editInfoForm.mobile,
        email: editInfoForm.email,
        picture: attachmentUpdates.pictureUrl || editInfoForm.pictureUrl || "",
        birthCert: attachmentUpdates.birthCertUrl || editInfoForm.birthCertUrl || "",
        schoolId: attachmentUpdates.schoolIdUrl || editInfoForm.schoolIdUrl || "",
        grades: attachmentUpdates.gradesUrl || editInfoForm.gradesUrl || "",
        pictureHardcopy: !!editInfoForm.pictureHardcopy,
        birthCertHardcopy: !!editInfoForm.birthCertHardcopy,
        schoolIdHardcopy: !!editInfoForm.schoolIdHardcopy,
        gradesHardcopy: !!editInfoForm.gradesHardcopy,
        preRegistration: editInfoForm.preRegistration,
        updatedAt: new Date().toISOString(),
      };
      await updateDoc(studentRef, updateData);
      const updatedStudent = {
        ...(localStudent || student || {}),
        ...updateData,
      };
      setLocalStudent(updatedStudent);
      setStudent(updatedStudent);
      writeNewStudentSession(updatedStudent);
      setEditInfoOpen(false);
    } catch (err) {
      console.error('Failed to save edited information:', err);
      setEditInfoError(err?.message || 'Unable to save information.');
    } finally {
      setEditInfoSaving(false);
    }
  };

  const handleToggleSameAsPresent = (checked) => {
    handleEditInfoField('preRegistration.sameAsPresent', checked);
    if (checked) {
      const present = editInfoForm.preRegistration.present;
      handleEditInfoField('preRegistration.permanent', { ...present });
    }
  };

  const handleEditInfoClose = () => {
    setEditInfoOpen(false);
    setEditInfoError("");
  };

  const handleProfileMenu = (action) => {
    setProfileMenuOpen(false);
    if (action === "logout") {
      console.log("[StudentDashboard] Logout button clicked");
      // Clear student portal flags first
      localStorage.removeItem("studentPortalLogin");
      localStorage.removeItem("currentStudentSession");
      // Then call SessionContext logout which will set logoutEvent
      logout();
      navigate("/signin", { replace: true });
      return;
    }
    if (action === "password") return setChangePassOpen(true);
    if (action === "edit") return setEditProfileOpen(true);
  };

  async function sendChangePasswordEmail() {
    setChangePassMsg("");
    const email = currentUser?.email || student?.email || localStudent?.email;
    if (!email) {
      setChangePassMsg("No email address is available for this student account.");
      return;
    }

    setChangingPass(true);
    try {
      const response = await fetch("https://us-central1-enrollease-19c30.cloudfunctions.net/sendPasswordResetEmail", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) throw new Error(await response.text());
      setChangePassMsg("Password reset email sent. Check your inbox or spam folder.");
      setTimeout(() => {
        setChangePassOpen(false);
        setChangePassMsg("");
      }, 1200);
    } catch (err) {
      console.error("sendChangePasswordEmail error:", err);
      setChangePassMsg(err?.message || "Unable to send the password reset email.");
    } finally {
      setChangingPass(false);
    }
  }

  // Fetch miscellaneous fees
  useEffect(() => {
    const db = getFirestore(app);
    const feesRef = doc(db, "systemSettings", "miscellaneousFees");
    const unsub = onSnapshot(feesRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setMiscFees(data.fees || []);
        setCashDiscount(data.cashDiscount || 0);
        setCashWithDiscountDate(data.cashWithDiscountDate || "");
        setCashWithoutDiscountDate(data.cashWithoutDiscountDate || "");
        setInstallmentDates(data.installmentDates || []);
      }
    });
    return () => unsub();
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: "#efefef", display: "flex", flexDirection: "row", fontFamily: "Inter, Arial, sans-serif" }}>
      {/* Sidebar (left) */}
      <div style={{ width: 240, background: "#0f5f3b", color: "#fff", display: "flex", flexDirection: "column", alignItems: "stretch", padding: "24px 0", minHeight: "100vh", position: "fixed", left: 0, top: 0, height: "100vh", zIndex: 1000 }}>
        <div style={{ fontWeight: 800, fontSize: 24, textAlign: "left", marginBottom: 24, letterSpacing: 1, padding: "4px 24px 0 24px" }}>EnrollEase</div>
        {/* No announcement/enrollment status in sidebar. */}
        {SIDEBAR_BUTTONS.map(btn => {
          // Determine disabled and opacity logic for each step
          let disabled = false;
          let cursor = "pointer";
          let opacity = 1;
          
          if (btn.key === "pre-registration" && (!canPreRegister || !enrollmentOpenFlag)) {
            disabled = true;
            cursor = "not-allowed";
            opacity = 0.5;
          }
          
          if (btn.key === "course-lineup") {
            // Only enable if pre-registration is submitted and enrollment is open
            if (preRegistrationStatus !== "submitted" || !enrollmentOpenFlag) {
              disabled = true;
              cursor = "not-allowed";
              opacity = 0.5;
            }
          }
          
          if (btn.key === "finances") {
            // Only enable if Course Line-up has been submitted
            const lineupDone = lineupSubmitted || 
              String(registrationStatus || '').toLowerCase() === 'submitted' || 
              String(registrationStatus || '').toLowerCase() === 'enrolled';
            
            if (!lineupDone || !enrollmentOpenFlag || preRegistrationStatus !== "submitted") {
              disabled = true;
              cursor = "not-allowed";
              opacity = 0.5;
            }
          }
          
          if (btn.key === "assessment") {
            // Disable if enrollment is closed
            if (!enrollmentOpenFlag) {
              disabled = true;
              cursor = "not-allowed";
              opacity = 0.5;
            }
          }
          
          if (btn.key === "final-enrollment") {
            // Check multiple sources for COR existence
            const hasCorData = Boolean(
              hasCor ||  // from real-time listener
              localStudent?.cor?.corPdfUrl ||
              student?.cor?.corPdfUrl ||
              String(registrationStatus || '').toLowerCase() === 'enrolled'
            );
            
            if (!hasCorData) {
              disabled = true;
              cursor = "not-allowed";
              opacity = 0.5;
            }
          }
          
          return (
            <button
              key={btn.key}
              onClick={() => {
                if (disabled) return;
                setActive(btn.key);
              }}
              style={{
                background: active === btn.key ? "#ff6f2e" : "transparent",
                color: active === btn.key ? "#fff" : "#fff",
                border: "none",
                borderRadius: 8,
                padding: "14px 24px",
                fontWeight: 600,
                fontSize: 15,
                cursor,
                textAlign: "left",
                transition: "all 0.2s",
                opacity,
                margin: "4px 16px",
                boxShadow: active === btn.key ? "0 2px 8px rgba(255,111,46,0.3)" : "none"
              }}
              disabled={disabled}
            >
              {btn.label}
            </button>
          );
        })}
        <div style={{ flex: 1 }} />
        <a
          href="https://online.mac.edu.ph/static/custom/AIMS-USER-GUIDE.pdf"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#fff",
            textDecoration: "none",
            padding: "14px 24px",
            fontWeight: 600,
            fontSize: 15,
            textAlign: "left",
            opacity: 0.95,
            margin: "4px 16px 60px 16px",
            borderRadius: 8,
            border: "1px solid rgba(255,255,255,0.6)"
          }}
        >
          ENROLLMENT GUIDE
        </a>
        {/* Remove old LOG OUT button, now in header dropdown */}
      </div>

      {/* Main area: header at top, content below */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh", marginLeft: 240 }}>
        {/* Header (top, profile on right) */}
        <div style={{ height: 80, background: "#fff", display: "flex", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", padding: "0 40px", position: "relative", zIndex: 10, justifyContent: "flex-end" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ marginRight: 16, fontWeight: 600, fontSize: 18, color: "#155c2b" }}>{student.firstName} {student.lastName}</span>
            <img
              src={profilePicUrl}
              alt="Profile"
              style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", cursor: "pointer", border: "2px solid #e67e22" }}
              onClick={() => setProfileMenuOpen((v) => !v)}
            />
          </div>
          {/* Profile Dropdown */}
          {profileMenuOpen && (
            <div style={{ position: "absolute", top: 60, right: 40, background: "#fff", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.13)", minWidth: 210, padding: "10px 0", zIndex: 100 }}>
              <div style={{ padding: "10px 24px", cursor: "pointer", color: "#222", fontWeight: 500 }} onClick={openEditInfoModal}>Edit Information</div>
              <div style={{ padding: "10px 24px", cursor: "pointer", color: "#222", fontWeight: 500 }} onClick={() => handleProfileMenu("edit")}>Edit Profile</div>
              <div style={{ padding: "10px 24px", cursor: "pointer", color: "#222", fontWeight: 500 }} onClick={() => handleProfileMenu("password")}>Change Password</div>
              <div style={{ height: 1, background: "#eee", margin: "6px 0" }} />
              <div style={{ padding: "10px 24px", cursor: "pointer", color: "#e67e22", fontWeight: 600 }} onClick={() => handleProfileMenu("logout")}>Log out</div>
            </div>
          )}

          {/* Edit Profile Modal */}
          {editProfileOpen && (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
              <div style={{ width: 520, background: "#fff", borderRadius: 12, padding: 24 }}>
                <h3 style={{ marginTop: 0 }}>Edit Profile</h3>
                <div style={{ height: 1, background: "#eee", margin: "12px 0" }} />
                <div style={{ display: 'flex', gap: 18, alignItems: 'center', marginTop: 12 }}>
                  <div style={{ width: 96, height: 96, borderRadius: '50%', overflow: 'hidden', border: '2px solid #e67e22' }}>
                    <img src={previewUrl || localStudent?.profilePicUrl || profilePicUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                  <div style={{ flex: 1 }}>
  
                    <div>
                      <label style={{ fontWeight: 500 }}>Profile picture</label>
                      <input type="file" accept="image/*" onChange={async (e) => {
                        const f = e.target.files && e.target.files[0];
                        if (f) {
                          // Simple client-side validation: limit to 5MB
                          if (f.size > 5 * 1024 * 1024) {
                            setEditMsg('Please select an image smaller than 5 MB.');
                            return;
                          }
                          setEditFile(f);
                          const url = URL.createObjectURL(f);
                          setPreviewUrl(url);
                        }
                      }} style={{ display: 'block', marginTop: 8 }} />
                      <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>Accepted: JPG/PNG. Max size 5 MB. Image will be resized before upload.</div>
                    </div>
                    {editMsg && <div style={{ marginTop: 8, color: editMsg.includes('success') ? 'green' : 'red' }}>{editMsg}</div>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                  <button onClick={() => { setEditProfileOpen(false); setEditFile(null); setPreviewUrl(''); setEditMsg(''); }} style={{ padding: '8px 14px' }}>Cancel</button>
                  <button onClick={async () => {
                    try {
                      setEditMsg('');
                      setUploading(true);
                      const db = getFirestore(app);
                      // resolve student doc by idNumber
                      let studentRef = null;
                      const q = query(collection(db, 'students'), where('idNumber', '==', student.idNumber), limit(1));
                      const snap = await getDocs(q);
                      if (!snap.empty) studentRef = snap.docs[0].ref;
                      else studentRef = doc(db, 'students', student.idNumber);

                      let profileUrl = localStudent?.profilePicUrl || null;
                      if (editFile) {
                        // Resize image client-side before uploading
                        const resizedBlob = await (async (file) => {
                          const img = document.createElement('img');
                          img.src = URL.createObjectURL(file);
                          await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
                          const maxDim = 512;
                          let { width, height } = img;
                          const ratio = Math.min(maxDim / width, maxDim / height, 1);
                          width = Math.round(width * ratio);
                          height = Math.round(height * ratio);
                          const canvas = document.createElement('canvas');
                          canvas.width = width;
                          canvas.height = height;
                          const ctx = canvas.getContext('2d');
                          ctx.drawImage(img, 0, 0, width, height);
                          const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.8));
                          URL.revokeObjectURL(img.src);
                          return blob;
                        })(editFile);

                        const storage = getStorage(app);
                        const sRef = storageRef(storage, `students/${studentRef.id || student.idNumber}/profile_${Date.now()}.jpg`);
                        await uploadBytes(sRef, resizedBlob);
                        profileUrl = await getDownloadURL(sRef);
                      }

                      await updateDoc(studentRef, { profilePicUrl: profileUrl });
                      setEditMsg('Profile updated successfully.');
                      // update local student immediately
                      setLocalStudent(s => ({ ...(s || {}), profilePicUrl: profileUrl }));
                      setTimeout(() => {
                        setEditProfileOpen(false);
                        setEditFile(null);
                        if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(''); }
                        setEditMsg('');
                      }, 900);
                    } catch (err) {
                      console.error('Error updating profile:', err);
                      setEditMsg(err?.message || 'Failed to update profile.');
                    } finally { setUploading(false); }
                  }} style={{ padding: '8px 14px', background: '#155c2b', color: '#fff', border: 'none', borderRadius: 6 }}>{uploading ? 'Saving...' : 'Save'}</button>
                </div>
              </div>
            </div>
          )}

          {changePassOpen && (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
              <div style={{ width: 420, background: "#fff", borderRadius: 12, padding: 24 }}>
                <h3 style={{ marginTop: 0 }}>Change password by email</h3>
                <div style={{ height: 1, background: "#eee", margin: "12px 0" }} />
                <div style={{ color: "#555", lineHeight: 1.6 }}>
                  We will send a secure password-change link to <strong>{currentUser?.email || student?.email || localStudent?.email || "your registered email"}</strong>.
                </div>
                {changePassMsg && <div style={{ marginTop: 12, color: changePassMsg.includes('sent') ? 'green' : 'red' }}>{changePassMsg}</div>}
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
                  <button onClick={() => { setChangePassOpen(false); setChangePassMsg(''); }} style={{ padding: "8px 14px" }}>Cancel</button>
                  <button onClick={sendChangePasswordEmail} disabled={changingPass} style={{ padding: "8px 14px", background: "#155c2b", color: "#fff", border: "none", borderRadius: 6 }}>{changingPass ? "Sending..." : "Send reset link"}</button>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Main Content */}
        <div style={{ flex: 1, padding: 40, display: "flex", flexDirection: "column", minHeight: "100vh" }}>
          {active === "home" && (
            <div>
              {/* Information Section */}
              <div style={{ background: '#f8fafb', borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.03)', marginBottom: 24, border: '1px solid #e5e7eb' }}>
                <div style={{ background: '#e8e8e8', color: '#155c2b', fontWeight: 700, fontSize: 22, padding: '12px 24px', borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>Information</div>
                <div style={{ padding: '24px 32px', display: 'flex', justifyContent: 'space-between', gap: 24, alignItems: 'flex-start', fontSize: 16, color: '#222' }}>
                  <div style={{ flex: 1, minWidth: 260 }}>
                    <div style={{ marginBottom: 6 }}><b>Full Name:</b> {student.firstName}, {student.lastName}</div>
                    <div style={{ marginBottom: 6 }}><b>ID Number:</b> {student.idNumber}</div>
                    <div style={{ marginBottom: 6 }}><b>Program:</b> {student.program}</div>
                    <div><b>Category:</b> {student.category || localStudent?.category || "-"}</div>
                  </div>
                </div>
              </div>
          {/* Announcement Section (now includes announcement and enrollment status) */}
          <div style={{ background: '#f8fafb', borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.03)', border: '1px solid #e5e7eb' }}>
            <div style={{ background: '#e8e8e8', color: '#155c2b', fontWeight: 700, fontSize: 22, padding: '12px 24px', borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>Announcement</div>
            <div style={{ padding: '32px 32px 16px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 180, position: 'relative' }}>
              <div style={{ background: '#fffbe6', color: '#b26a00', border: '1px solid #ffe58f', borderRadius: 8, padding: '16px 24px', fontWeight: 600, fontSize: 17, textAlign: 'center', marginBottom: 24 }}>
                {announcement && <div>{announcement}</div>}
                <div style={{ fontWeight: 500, fontSize: 15, marginTop: announcement ? 6 : 0, color: enrollmentOpenFlag ? '#18803b' : '#b71c1c' }}>
                  {getEnrollmentStatusMessage()}
                </div>
                {!localStudent?.passwordChanged && (
                  <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #ffe58f', fontSize: 14, lineHeight: 1.6, color: '#5f4b00', textAlign: 'left' }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>🔔 Important Notice</div>
                    <div>For your account security, please <strong>change your temporary password</strong> after logging in. Go to your <strong>Profile icon</strong> and select <strong>Change Password</strong>.</div>
                  </div>
                )}
              </div>
              {/* When enrollment is closed, hide all other announcement content below */}
              {enrollmentOpenFlag ? (
                <>
                  {/* Show Profile Information Complete after pre-registration is done (real-time) */}
                  {canFinalize ? (
                    <>
                      <div style={{ fontSize: 22, color: '#0e0d0d', fontWeight: 500, marginBottom: 24, marginTop: 30, textAlign: 'center' }}>
                        You are now officially enrolled! Please check your email for your student portal.
                      </div>
                    </>
                  ) : preRegistrationStatus === "submitted" ? (
                     <>
                       <div style={{ fontSize: 22, color: '#0e0d0dff', fontWeight: 500, marginBottom: 24, marginTop: 30, textAlign: 'center' }}>
                         {lineupSubmitted ? (
                           <>Your miscellaneous fees is now available. Please review your Finance</>
                         ) : localSchedules && localSchedules.length > 0 ? (
                           <>
                             Course Line-up Complete.<br />
                             Please check your Course Line-up then press Submit to confirm.
                           </>
                         ) : (
                           <>
                             Profile Information Complete.<br />
                             Please wait for your Course Line-up.
                           </>
                         )}
                       </div>
                     </>
                   ) : assessmentStatus === "approved" ? (
                     <>
                       <div style={{ fontSize: 22, color: '#1a2b3c', fontWeight: 500, marginBottom: 24, marginTop: 30, textAlign: 'center' }}>
                         Your assessment has been approved.
                       </div>
                       <button
                         style={{ background: '#ff9800', color: '#fff', border: 'none', borderRadius: 4, padding: '12px 32px', marginBottom: 40, fontWeight: 700, fontSize: 16, cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.07)' }}
                         onClick={() => setActive('pre-registration')}
                       >
                         PROCEED TO ENROLLMENT
                       </button>
                       {/* schedule card intentionally removed when approved */}
                     </>
                   ) : assessmentStatus === "disapproved" ? (
                     <>
                       <div style={{ fontSize: 22, color: '#b71c1c', fontWeight: 500, marginBottom: 24, marginTop: 30, textAlign: 'center' }}>
                         We regret to inform you that your assessment has been disapproved.<br />
                         You can retry your exam by booking an new appointment in Assessment.<br />
                         Please contact the Admission Office for more information.<br />
                         <b>admission@mac.com</b>
                       </div>
                     </>
                   ) : (assessmentSchedule && assessmentStatus !== "disapproved") ? (
                    <div style={{ width: '100%' }}>
                      <div style={{ fontWeight: 600, color: "#155c2b", fontSize: 20, marginBottom: 30, marginTop: 30, textAlign: 'center' }}>Competency Assessment Schedule</div>
                      <div style={{ fontSize: 16, marginBottom: 10, textAlign: 'center' }}><b>Date:</b> {formatDate(assessmentSchedule.date)}</div>
                      <div style={{ fontSize: 16, marginBottom: 10, textAlign: 'center' }}><b>Time:</b> {formatTime(assessmentSchedule.time)}</div>
                      <div style={{ fontSize: 16, marginBottom: 10, textAlign: 'center' }}><b>Place:</b> Admission Services Office Manila Adventist College</div>
                      <div style={{ fontSize: 16, marginBottom: 10, textAlign: 'center' }}><b>Address:</b> 1975 San Juan, Pasay City, 1300 Kalakhang Manila</div>
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: 22, color: '#1a2b3c', fontWeight: 500, marginBottom: 24, marginTop: 30,  textAlign: 'center' }}>
                        Assessment is now open for AY 2025-2026-First Semester
                      </div>
                      <button
                        style={{ background: '#ff9800', color: '#fff', border: 'none', borderRadius: 4, padding: '12px 32px', marginBottom: 40, fontWeight: 700, fontSize: 16, cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.07)' }}
                        onClick={() => setActive('assessment')}
                      >
                        PROCEED TO ASSESSMENT
                      </button>
                    </>
                  )}
                </>
              ) : null}
            </div>
          </div>
            </div>
          )}
          {/* Schedule Modal */}
          {showScheduleModal && (
            <BookAssessmentModal
              studentId={student.idNumber}
              appointmentStart={currentSemesterDates.appointmentStart}
              appointmentEnd={currentSemesterDates.appointmentEnd}
              onClose={async (date, time) => {
                setShowScheduleModal(false);
              }}
            />
          )}
          {editInfoOpen && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
              <form className="edit-info-modal" onSubmit={handleSaveEditInfo} style={{ width: '100%', maxWidth: 1100, maxHeight: '90vh', overflowY: 'auto', overflowX: 'auto', background: '#fff', borderRadius: 16, padding: 28, boxSizing: 'border-box', boxShadow: '0 16px 48px rgba(0,0,0,0.18)' }}>
                <style>{`.edit-info-modal label { display: block; margin-bottom: 10px; min-width: 0; }
                .edit-info-modal input[type=text], .edit-info-modal input[type=email], .edit-info-modal input[type=date], .edit-info-modal select, .edit-info-modal input[type=file] { width: 100%; padding: 10px; margin-top: 6px; border-radius: 6px; box-sizing: border-box; min-height: 40px; font-size: 14px; border: 1px solid #ccc; }
                .edit-info-modal input[type=checkbox] { width: auto; margin-right: 8px; }
                .edit-info-modal input[readonly] { background: #f7f7f7; color: #111; }
                .edit-info-modal .error-field { border: 2px solid red !important; }
                .edit-info-modal .section-panel { min-width: 0; }
                .edit-info-modal .grid-fit { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 24px; width: 100%; }
                .edit-info-modal .section-panel-inner { background: #f8fafb; border-radius: 12px; border: 1px solid #e5e7eb; padding: 18px; min-width: 0; }
                .edit-info-modal .inline-label { display: inline-flex; align-items: center; gap: 8px; }`}</style>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 22 }}>
                  <div style={{ minWidth: 0 }}>
                    <h3 style={{ margin: 0, fontSize: 24, color: '#155c2b' }}>Edit Student Information</h3>
                    <div style={{ marginTop: 4, color: '#666', fontSize: 14 }}>Update application and pre-registration data, then click Save to save.</div>
                  </div>
                  <button type="button" onClick={handleEditInfoClose} style={{ background: 'transparent', border: 'none', fontSize: 24, lineHeight: 1, cursor: 'pointer', color: '#444' }}>×</button>
                </div>

                <div className="grid-fit" style={{ marginBottom: 24 }}>
                  <div className="section-panel-inner">
                    <div style={{ fontWeight: 700, marginBottom: 10 }}>Application Details</div>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      Category
                      <input type="text" readOnly value={editInfoForm.category || ''} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc', background: '#f7f7f7', color: '#111' }} />
                    </label>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      Program
                      <input type="text" readOnly value={editInfoForm.program || ''} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc', background: '#f7f7f7', color: '#111' }} />
                    </label>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      First Name
                      <input type="text" value={editInfoForm.firstName} onChange={(e) => handleEditInfoField('firstName', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('firstName') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                      {getEditFieldError('firstName') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('firstName')}</div>}
                    </label>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      Middle Name
                      <input type="text" value={editInfoForm.middleName} onChange={(e) => handleEditInfoField('middleName', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('middleName') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                      {getEditFieldError('middleName') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('middleName')}</div>}
                    </label>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      <input type="checkbox" checked={editInfoForm.middleNameNA} onChange={(e) => handleEditInfoField('middleNameNA', e.target.checked)} style={{ marginRight: 8 }} />
                      Middle Name N/A
                    </label>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      Last Name
                      <input type="text" value={editInfoForm.lastName} onChange={(e) => handleEditInfoField('lastName', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('lastName') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                      {getEditFieldError('lastName') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('lastName')}</div>}
                    </label>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      Gender
                      <select value={editInfoForm.gender} onChange={(e) => handleEditInfoField('gender', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('gender') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }}>
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                      {getEditFieldError('gender') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('gender')}</div>}
                    </label>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      Date of Birth
                      <input type="date" value={editInfoForm.dob} onChange={(e) => handleEditInfoField('dob', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('dob') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                      {getEditFieldError('dob') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('dob')}</div>}
                    </label>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      Mobile Number
                      <input type="text" value={editInfoForm.mobile} onChange={(e) => handleEditInfoField('mobile', e.target.value)} placeholder="09XXXXXXXXX" style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('mobile') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                      {getEditFieldError('mobile') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('mobile')}</div>}
                    </label>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      Email
                      <input type="email" value={editInfoForm.email} onChange={(e) => handleEditInfoField('email', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('email') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                      {getEditFieldError('email') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('email')}</div>}
                    </label>
                  </div>

                  <div className="section-panel-inner" style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, marginBottom: 10 }}>Pre-Registration</div>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      Entry Level
                      <input type="text" readOnly value={editInfoForm.preRegistration.entryLevel || ''} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc', background: '#f7f7f7', color: '#111' }} />
                    </label>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      Semester
                      <input type="text" readOnly value={editInfoForm.preRegistration.semester || ''} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc', background: '#f7f7f7', color: '#111' }} />
                    </label>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      Nationality
                      <input type="text" value={editInfoForm.preRegistration.nationality} onChange={(e) => handleEditInfoField('preRegistration.nationality', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.nationality') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                      {getEditFieldError('preRegistration.nationality') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.nationality')}</div>}
                    </label>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      Religion
                      <input type="text" value={editInfoForm.preRegistration.religion} onChange={(e) => handleEditInfoField('preRegistration.religion', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.religion') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                      {getEditFieldError('preRegistration.religion') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.religion')}</div>}
                    </label>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      Civil Status
                      <input type="text" value={editInfoForm.preRegistration.civilStatus} onChange={(e) => handleEditInfoField('preRegistration.civilStatus', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc' }} />
                    </label>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      Birth Place
                      <input type="text" value={editInfoForm.preRegistration.birthPlace} onChange={(e) => handleEditInfoField('preRegistration.birthPlace', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.birthPlace') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                      {getEditFieldError('preRegistration.birthPlace') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.birthPlace')}</div>}
                    </label>
                  </div>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontWeight: 700, marginBottom: 10 }}>Address</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24, width: '100%' }}>
                    <div style={{ minWidth: 0, background: '#f8fafb', borderRadius: 12, border: '1px solid #e5e7eb', padding: 18 }}>
                      <div style={{ fontWeight: 700, marginBottom: 12 }}>Present Address</div>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        House/Street
                        <input type="text" value={editInfoForm.preRegistration.present.house} onChange={(e) => handleEditInfoField('preRegistration.present.house', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc' }} />
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Barangay
                        <input type="text" value={editInfoForm.preRegistration.present.barangay} onChange={(e) => handleEditInfoField('preRegistration.present.barangay', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc' }} />
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        City
                        <input type="text" value={editInfoForm.preRegistration.present.city} onChange={(e) => handleEditInfoField('preRegistration.present.city', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.present.city') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                        {getEditFieldError('preRegistration.present.city') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.present.city')}</div>}
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Province
                        <input type="text" value={editInfoForm.preRegistration.present.province} onChange={(e) => handleEditInfoField('preRegistration.present.province', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.present.province') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                        {getEditFieldError('preRegistration.present.province') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.present.province')}</div>}
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Postal Code
                        <input type="text" value={editInfoForm.preRegistration.present.postal} onChange={(e) => handleEditInfoField('preRegistration.present.postal', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc' }} />
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Country
                        <input type="text" value={editInfoForm.preRegistration.present.country} onChange={(e) => handleEditInfoField('preRegistration.present.country', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.present.country') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                        {getEditFieldError('preRegistration.present.country') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.present.country')}</div>}
                      </label>
                    </div>
                    <div style={{ minWidth: 0, background: '#f8fafb', borderRadius: 12, border: '1px solid #e5e7eb', padding: 18 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div style={{ fontWeight: 700 }}>Permanent Address</div>
                        <label style={{ fontSize: 13, color: '#333' }}>
                          <input type="checkbox" checked={editInfoForm.preRegistration.sameAsPresent} onChange={(e) => handleToggleSameAsPresent(e.target.checked)} style={{ marginRight: 8 }} /> Same as present
                        </label>
                      </div>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        House/Street
                        <input type="text" value={editInfoForm.preRegistration.permanent.house} onChange={(e) => handleEditInfoField('preRegistration.permanent.house', e.target.value)} disabled={editInfoForm.preRegistration.sameAsPresent} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc', background: editInfoForm.preRegistration.sameAsPresent ? '#f5f5f5' : '#fff' }} />
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Barangay
                        <input type="text" value={editInfoForm.preRegistration.permanent.barangay} onChange={(e) => handleEditInfoField('preRegistration.permanent.barangay', e.target.value)} disabled={editInfoForm.preRegistration.sameAsPresent} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc', background: editInfoForm.preRegistration.sameAsPresent ? '#f5f5f5' : '#fff' }} />
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        City
                        <input type="text" value={editInfoForm.preRegistration.permanent.city} onChange={(e) => handleEditInfoField('preRegistration.permanent.city', e.target.value)} disabled={editInfoForm.preRegistration.sameAsPresent} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.permanent.city') ? '2px solid red' : '1px solid #ccc', background: editInfoForm.preRegistration.sameAsPresent ? '#f5f5f5' : '#fff' }} />
                        {getEditFieldError('preRegistration.permanent.city') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.permanent.city')}</div>}
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Province
                        <input type="text" value={editInfoForm.preRegistration.permanent.province} onChange={(e) => handleEditInfoField('preRegistration.permanent.province', e.target.value)} disabled={editInfoForm.preRegistration.sameAsPresent} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.permanent.province') ? '2px solid red' : '1px solid #ccc', background: editInfoForm.preRegistration.sameAsPresent ? '#f5f5f5' : '#fff' }} />
                        {getEditFieldError('preRegistration.permanent.province') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.permanent.province')}</div>}
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Postal Code
                        <input type="text" value={editInfoForm.preRegistration.permanent.postal} onChange={(e) => handleEditInfoField('preRegistration.permanent.postal', e.target.value)} disabled={editInfoForm.preRegistration.sameAsPresent} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc', background: editInfoForm.preRegistration.sameAsPresent ? '#f5f5f5' : '#fff' }} />
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Country
                        <input type="text" value={editInfoForm.preRegistration.permanent.country} onChange={(e) => handleEditInfoField('preRegistration.permanent.country', e.target.value)} disabled={editInfoForm.preRegistration.sameAsPresent} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.permanent.country') ? '2px solid red' : '1px solid #ccc', background: editInfoForm.preRegistration.sameAsPresent ? '#f5f5f5' : '#fff' }} />
                        {getEditFieldError('preRegistration.permanent.country') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.permanent.country')}</div>}
                      </label>
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontWeight: 700, marginBottom: 10 }}>Education</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
                    <div style={{ minWidth: 0, background: '#f8fafb', borderRadius: 12, border: '1px solid #e5e7eb', padding: 18 }}>
                      <div style={{ fontWeight: 700, marginBottom: 12 }}>Elementary</div>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        School
                        <input type="text" value={editInfoForm.preRegistration.elementary.school} onChange={(e) => handleEditInfoField('preRegistration.elementary.school', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.elementary.school') ? '2px solid red' : '1px solid #ccc' }} />
                        {getEditFieldError('preRegistration.elementary.school') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.elementary.school')}</div>}
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Address
                        <input type="text" value={editInfoForm.preRegistration.elementary.address} onChange={(e) => handleEditInfoField('preRegistration.elementary.address', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc' }} />
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Year
                        <input type="text" value={editInfoForm.preRegistration.elementary.year} onChange={(e) => handleEditInfoField('preRegistration.elementary.year', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.elementary.year') ? '2px solid red' : '1px solid #ccc' }} />
                        {getEditFieldError('preRegistration.elementary.year') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.elementary.year')}</div>}
                      </label>
                    </div>
                    <div style={{ minWidth: 0, background: '#f8fafb', borderRadius: 12, border: '1px solid #e5e7eb', padding: 18 }}>
                      <div style={{ fontWeight: 700, marginBottom: 12 }}>Junior High School</div>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        School
                        <input type="text" value={editInfoForm.preRegistration.junior.school} onChange={(e) => handleEditInfoField('preRegistration.junior.school', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.junior.school') ? '2px solid red' : '1px solid #ccc' }} />
                        {getEditFieldError('preRegistration.junior.school') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.junior.school')}</div>}
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Address
                        <input type="text" value={editInfoForm.preRegistration.junior.address} onChange={(e) => handleEditInfoField('preRegistration.junior.address', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc' }} />
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Year
                        <input type="text" value={editInfoForm.preRegistration.junior.year} onChange={(e) => handleEditInfoField('preRegistration.junior.year', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.junior.year') ? '2px solid red' : '1px solid #ccc' }} />
                        {getEditFieldError('preRegistration.junior.year') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.junior.year')}</div>}
                      </label>
                    </div>
                    <div style={{ minWidth: 0, background: '#f8fafb', borderRadius: 12, border: '1px solid #e5e7eb', padding: 18 }}>
                      <div style={{ fontWeight: 700, marginBottom: 12 }}>Senior High School</div>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        School
                        <input type="text" value={editInfoForm.preRegistration.senior.school} onChange={(e) => handleEditInfoField('preRegistration.senior.school', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.senior.school') ? '2px solid red' : '1px solid #ccc' }} />
                        {getEditFieldError('preRegistration.senior.school') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.senior.school')}</div>}
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Address
                        <input type="text" value={editInfoForm.preRegistration.senior.address} onChange={(e) => handleEditInfoField('preRegistration.senior.address', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc' }} />
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Year
                        <input type="text" value={editInfoForm.preRegistration.senior.year} onChange={(e) => handleEditInfoField('preRegistration.senior.year', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.senior.year') ? '2px solid red' : '1px solid #ccc' }} />
                        {getEditFieldError('preRegistration.senior.year') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.senior.year')}</div>}
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Strand
                        <input type="text" value={editInfoForm.preRegistration.senior.strand} onChange={(e) => handleEditInfoField('preRegistration.senior.strand', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.senior.strand') ? '2px solid red' : '1px solid #ccc' }} />
                        {getEditFieldError('preRegistration.senior.strand') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.senior.strand')}</div>}
                      </label>
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontWeight: 700, marginBottom: 10 }}>Family Information</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(280px, 1fr))', gap: 24, width: '100%' }}>
                    <label style={{ display: 'block', marginBottom: 10, minWidth: 0 }}>
                      Parent/Guardian Name
                      <input type="text" value={editInfoForm.preRegistration.parentName} onChange={(e) => handleEditInfoField('preRegistration.parentName', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.parentName') ? '2px solid red' : '1px solid #ccc' }} />
                      {getEditFieldError('preRegistration.parentName') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.parentName')}</div>}
                    </label>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      Relationship
                      <input type="text" value={editInfoForm.preRegistration.relationship} onChange={(e) => handleEditInfoField('preRegistration.relationship', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.relationship') ? '2px solid red' : '1px solid #ccc' }} />
                      {getEditFieldError('preRegistration.relationship') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.relationship')}</div>}
                    </label>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      Occupation
                      <input type="text" value={editInfoForm.preRegistration.occupation} onChange={(e) => handleEditInfoField('preRegistration.occupation', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.occupation') ? '2px solid red' : '1px solid #ccc' }} />
                      {getEditFieldError('preRegistration.occupation') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.occupation')}</div>}
                    </label>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 3fr', gap: 24, width: '100%', alignItems: 'start' }}>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      Family Mobile
                      <input type="text" value={editInfoForm.preRegistration.familyMobile} onChange={(e) => handleEditInfoField('preRegistration.familyMobile', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.familyMobile') ? '2px solid red' : '1px solid #ccc' }} />
                      {getEditFieldError('preRegistration.familyMobile') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.familyMobile')}</div>}
                    </label>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      Family Address
                      <input type="text" value={editInfoForm.preRegistration.familyAddress} onChange={(e) => handleEditInfoField('preRegistration.familyAddress', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.familyAddress') ? '2px solid red' : '1px solid #ccc' }} />
                      {getEditFieldError('preRegistration.familyAddress') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.familyAddress')}</div>}
                    </label>
                  </div>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontWeight: 700, marginBottom: 10 }}>Attachments</div>
                  <div style={{ fontSize: 14, color: '#666', marginBottom: 12 }}>
                    Upload attachments accept JPG, PNG, PDF type (filesize: 5MB and below). Only empty attachment slots may be filled; existing uploaded files cannot be changed here.
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24, width: '100%' }}>
                    {[
                      { key: 'picture', label: '2x2 Picture', hardcopyLabel: 'I will submit a physical hard copy later' },
                      { key: 'birthCert', label: 'Birth/Marriage Certificate', hardcopyLabel: 'I will submit a physical hard copy later' },
                      { key: 'schoolId', label: 'School ID/Government ID', hardcopyLabel: 'I will submit a physical hard copy later' },
                      { key: 'grades', label: 'Copy of Grades', hardcopyLabel: 'I will submit a physical hard copy later' },
                    ].map(({ key, label, hardcopyLabel }) => {
                      const hasUrl = !!editInfoForm[`${key}Url`];
                      const hardcopyChecked = editInfoForm[`${key}Hardcopy`];
                      return (
                        <div key={key} style={{ background: '#f8fafb', borderRadius: 12, border: '1px solid #e5e7eb', padding: 18 }}>
                          <label style={{ fontWeight: 600 }}>{label}</label>
                          {editInfoFieldErrors[key] && <div style={{ color: '#d32f2f', fontSize: 12, marginTop: 6 }}>{editInfoFieldErrors[key]}</div>}
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png,.pdf"
                            disabled={hasUrl || hardcopyChecked}
                            onChange={(e) => handleEditInfoFile(key, e.target.files?.[0])}
                            style={{ width: '100%', marginTop: 8 }}
                          />
                          <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                            {hasUrl && (
                              <span>Already uploaded. <a href={editInfoForm[`${key}Url`]} target="_blank" rel="noreferrer" style={{ color: '#155c2b' }}>View</a></span>
                            )}
                            {hardcopyChecked && !hasUrl && <span>Physical hard copy will be submitted later.</span>}
                            {!hasUrl && !hardcopyChecked && <span>Only upload if this slot is empty.</span>}
                          </div>
                          <label style={{ marginTop: 12, fontSize: 13, display: 'block' }}>
                            <input
                              type="checkbox"
                              checked={hardcopyChecked}
                              disabled={hasUrl}
                              onChange={(e) => handleToggleHardcopy(key, e.target.checked)}
                              style={{ marginRight: 8 }}
                            />
                            {hardcopyLabel}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {editInfoError && <div style={{ color: '#d32f2f', marginBottom: 18 }}>{editInfoError}</div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <button type="button" onClick={handleEditInfoClose} style={{ background: '#f5f5f5', color: '#333', border: '1px solid #ccc', borderRadius: 8, padding: '12px 22px', cursor: 'pointer' }}>Cancel</button>
                  <button type="submit" disabled={editInfoSaving} style={{ background: editInfoSaving ? '#ccc' : '#155c2b', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 28px', fontWeight: 700, cursor: editInfoSaving ? 'not-allowed' : 'pointer' }}>{editInfoSaving ? 'Saving...' : 'Save'}</button>
                </div>
              </form>
            </div>
          )}
          {/* Placeholder for other sections */}
          {active === "assessment" && (
            <div>
              {/* Competency Assessment and Schedule Side by Side */}
              <div style={{ display: 'flex', gap: 32, marginBottom: 32 }}>
                {/* Competency Assessment Section */}
                <div style={{ flex: 1, background: "#fff", borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", padding: 32 }}>
                  <h3 style={{ color: "#155c2b", fontWeight: 700, marginBottom: 18, fontSize: 19 }}>Competency Assessment</h3>
                  <div style={{ fontSize: 16, color: "#222" }}>
                    <ul style={{ margin: "12px 0 0 18px" }}>
                      <li>
                        {assessmentSchedule == null || assessmentStatus === "disapproved" ? (
                          <>
                            Choose your preferred schedule <span style={{ color: "#fff", background: "#ff9800", borderRadius: 4, padding: "2px 8px", fontSize: 13, cursor: "pointer" }} onClick={() => setShowScheduleModal(true)}>here</span>
                          </>
                        ) : (
                          <>You have already booked your schedule.</>
                        )}
                      </li>
                      <li>On your approved schedule, proceed first to the <b>Finances</b>, onsite payment only (Php 500).</li>
                      <li>Show your receipt to the Admission Services Office for the Competency Assessment.</li>
                      <li>Once you're done with the assessment, you can continue your enrollment.</li>
                    </ul>
                    <div style={{ fontSize: 13, color: "#888", marginTop: 18 }}>
                      For inquiry about the Competency Assessment, contact the Admission Services Office.<br />
                      <span>
                        <b>macpsychcenter@mac.edu.ph</b>
                        <br />
                        Telephone: 02-8525-9191 local 281
                      </span>
                    </div>    
                  </div>
                </div>
                {/* Assessment Schedule Card */}
                {assessmentSchedule && (
                  <div
                    style={{
                      background: "#fff",
                      borderRadius: 8,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
                      padding: 24,
                      minWidth: 400,
                      maxWidth: 500,
                      alignSelf: 'stretch',
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%'
                    }}
                  >
                    <div style={{ fontWeight: 600, color: "#155c2b", fontSize: 20, marginBottom: 50, marginTop: 30 }}>Competency Assessment Schedule</div>
                    <div style={{ fontSize: 16, marginBottom: 10 }}><b>Date:</b> {formatDate(assessmentSchedule.date)}</div>
                    <div style={{ fontSize: 16, marginBottom: 10 }}><b>Time:</b> {formatTime(assessmentSchedule.time)}</div>
                    <div style={{ fontSize: 16, marginBottom: 10 }}><b>Place:</b> Admission Services Office Manila Adventist College</div>
                    <div style={{ fontSize: 16,  marginBottom: 50 }}><b>Address:</b> 1975 San Juan, Pasay City, 1300 Kalakhang Manila</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {active === "pre-registration" && (
            <PreRegistrationProfileInfo studentId={student.idNumber} setActive={setActive} />
          )}
          {active === "course-lineup" && (
            <CourseLineup studentId={student.idNumber} onSubmit={() => {
              setLineupSubmitted(true);
              setActive("finances");
              setLocalSchedules((prev) => prev && prev.length > 0 ? prev : [{}]);
            }} />
          )}
          {active === "finances" && (
            <StudentFinance studentId={student.idNumber} />
          )}
          {active === "final-enrollment" && (
            <FinalEnrollment
      student={localStudent}  // CHANGE from student to localStudent
      miscFees={miscFees}
      cashDiscount={cashDiscount}
      cashWithDiscountDate={cashWithDiscountDate}
      cashWithoutDiscountDate={cashWithoutDiscountDate}
      installmentDates={installmentDates}
    />
          )} 
        </div>
      </div>
    </div>
  );
}


// Modal for booking assessment schedule
function BookAssessmentModal({ onClose, studentId, appointmentStart, appointmentEnd }) {
  const todayKey = useCalendarClock();
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [isFriday, setIsFriday] = useState(false);

  const [amCount, setAmCount] = useState(0);
  const [pmCount, setPmCount] = useState(0);
  const [loadingCounts, setLoadingCounts] = useState(false);

  // Compute the earliest selectable date based on the appointment window and current time.
  const computeEarliestDate = () => {
    const now = new Date();
    const start = calendarDateToLocalDate(appointmentStart);
    const end = calendarDateToLocalDate(appointmentEnd);

    const toLocalDate = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const pad = (n) => String(n).padStart(2, '0');
    const formatLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    const today = calendarDateToLocalDate(todayKey) || toLocalDate(now);
    // Require at least one-day lead time: earliest selectable date is tomorrow
    const earliestAllowed = new Date(today);
    earliestAllowed.setDate(earliestAllowed.getDate() + 1);

    let candidate = start ? toLocalDate(start) : earliestAllowed;
    if (candidate < earliestAllowed) candidate = earliestAllowed;

    const dayHasSlot = (dateObj) => {
      const day = dateObj.getDay();
      return day !== 0 && day !== 6;
    };

    // Advance candidate until a day with at least one slot is found or we exceed end date
    while (end && candidate.getTime() <= toLocalDate(end).getTime() && !dayHasSlot(candidate)) {
      candidate = new Date(candidate);
      candidate.setDate(candidate.getDate() + 1);
    }

    const noAvailable = end && candidate.getTime() > toLocalDate(end).getTime();
    return { dateStr: formatLocal(candidate), noAvailable };
  };

  const { dateStr: computedMinDateStr, noAvailable: noAppointmentSlots } = computeEarliestDate();

  // Fetch number of applicants for the selected date and slot (exclude disapproved)
  const fetchSlotCounts = async (dateStr) => {
    if (!dateStr) {
      setAmCount(0);
      setPmCount(0);
      return;
    }
    setLoadingCounts(true);
    try {
      const db = getFirestore(app);
      const qAm = query(collection(db, "students"), where("appointment.date", "==", dateStr), where("appointment.time", "==", "am"));
      const qPm = query(collection(db, "students"), where("appointment.date", "==", dateStr), where("appointment.time", "==", "pm"));
      const [snapAm, snapPm] = await Promise.all([getDocs(qAm), getDocs(qPm)]);
      const countFilter = (snap) => snap.docs.filter(d => {
        const s = d.data().appointment?.status;
        return String(s || "").toLowerCase() !== "disapproved";
      }).length;
      setAmCount(countFilter(snapAm));
      setPmCount(countFilter(snapPm));
    } catch (err) {
      console.error("Failed to fetch slot counts:", err);
      setAmCount(0);
      setPmCount(0);
    } finally {
      setLoadingCounts(false);
    }
  };

  const appointmentOpen = () => {
    return isCalendarDateInRange(appointmentStart, appointmentEnd, todayKey);
  };

  const handleDateChange = (e) => {
    const val = e.target.value;
    setError("");
    if (!val) {
      setDate("");
      setIsFriday(false);
      setTime("");
      fetchSlotCounts("");
      return;
    }
    const sel = calendarDateToLocalDate(val);
    const day = sel.getDay(); // 0 = Sun, 1 = Mon, ..., 5 = Fri, 6 = Sat
    if (day === 0 || day === 6) {
      setError('Selected date falls on a weekend (Saturday/Sunday). Please choose a weekday.');
      setDate("");
      setIsFriday(false);
      setTime("");
      fetchSlotCounts("");
      return;
    }
    setDate(val);
    // fetch counts for this date
    fetchSlotCounts(val);
    if (day === 5) {
      // Friday: force AM only
      setIsFriday(true);
      setTime('am');
    } else {
      setIsFriday(false);
      // For future dates, clear time so user can choose
      setTime('');
    }
  };

  // resolve the correct students/{docId} reference by trying:
  // 1) direct doc id, 2) idNumber field, 3) uid field, 4) fallback direct ref
  async function resolveStudentRef(db, studentId) {
    const directRef = doc(db, "students", studentId);
    const directSnap = await getDoc(directRef);
    if (directSnap.exists()) return directRef;

    const qId = query(collection(db, "students"), where("idNumber", "==", studentId), limit(1));
    const snapById = await getDocs(qId);
    if (!snapById.empty) return snapById.docs[0].ref;

    const qUid = query(collection(db, "students"), where("uid", "==", studentId), limit(1));
    const snapByUid = await getDocs(qUid);
    if (!snapByUid.empty) return snapByUid.docs[0].ref;

    return directRef;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!date || !time) {
      setError("Please select a date and time.");
      return;
    }
    // Prevent booking a slot that's already full
    if ((time === 'am' && amCount >= 30) || (time === 'pm' && pmCount >= 30)) {
      setError("Selected slot is already full. Please choose another date or time.");
      return;
    }
    if (!studentId) {
      setError("Student ID missing. Please reload the page.");
      return;
    }
    // Validate date within appointment window if configured
    if (appointmentStart && appointmentEnd) {
      const selectedKey = toCalendarDateKey(date);
      const startKey = toCalendarDateKey(appointmentStart);
      const endKey = toCalendarDateKey(appointmentEnd);
      if (!selectedKey || selectedKey < startKey || selectedKey > endKey) {
        setError("Selected date is outside the appointment booking window.");
        return;
      }
      const sel = calendarDateToLocalDate(date);
      const day = sel.getDay();
      // Disallow weekends
      if (day === 0 || day === 6) {
        setError('Selected date falls on a weekend (Saturday/Sunday). Please choose a weekday.');
        return;
      }
      // If Friday (5), ensure AM only
      if (day === 5 && time !== 'am') {
        setError('On Fridays, only AM schedule is allowed.');
        return;
      }
    }
    if (!appointmentOpen()) {
      setError("Appointment booking is currently closed.");
      return;
    }
    setSubmitting(true);
    try {
      const db = getFirestore(app);
      const studentRef = await resolveStudentRef(db, studentId);

      // use setDoc with merge so it updates existing doc or creates/merges if missing
      await setDoc(studentRef, {
        appointment: { date, time, status: "pending" }
      }, { merge: true });

      // Update local counts immediately so UI reflects new booking without awaiting another query
      if (time === 'am') setAmCount((c) => c + 1);
      if (time === 'pm') setPmCount((c) => c + 1);

      setSubmitting(false);
      // give real-time listener a moment to update parent state
      setTimeout(() => onClose(date, time), 300);
    } catch (err) {
      console.error("Failed to save appointment:", err);
      setSubmitting(false);
      setError(err?.message || "Failed to save appointment. Please try again.");
    }
  };

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.25)", zIndex: 9999,
      display: "flex", alignItems: "center", justifyContent: "center"
    }}>
      <form onSubmit={handleSubmit} style={{ background: "#fff", borderRadius: 12, minWidth: 380, minHeight: 180, padding: 32, boxShadow: "0 4px 24px rgba(0,0,0,0.12)", position: "relative", fontSize: 15 }}>
        <h3 style={{ fontWeight: 700, fontSize: 20, marginBottom: 12 }}>Book an Appointment</h3>
        <ul style={{ color: "#444", fontSize: 14, marginBottom: 16, paddingLeft: 18 }}>
          <li>Selected schedule before the opening date will be invalid.</li>
          <li>Please select a date between Mondays-Thursdays only, except holidays.</li>
          <li>For Friday, select AM schedule only.</li>
          <li>30 applicants per schedule only.</li>
        </ul>
        <div style={{ marginBottom: 18 }}>
          <label style={{ fontWeight: 500 }}>Choose date Schedule</label><br />
          <input
            type="date"
            value={date}
            min={computedMinDateStr}
            max={appointmentEnd ? appointmentEnd.split('T')[0] : ''}
            onChange={handleDateChange}
            style={{ fontSize: 15, padding: 6, marginTop: 4, width: 180 }}
          />
          <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
            {date ? (
              loadingCounts ? 'Checking availability...' : <span>Slots: AM {amCount}/30 · PM {pmCount}/30</span>
            ) : 'Select a date to see availability. '}
            <br />
            Note: Saturdays and Sundays are not allowed. On Fridays, only the AM slot is available. Please book at least one day in advance.
          </div>
          {noAppointmentSlots && (
            <div style={{ color: 'red', marginTop: 8 }}>
              No appointment slots are available in the configured booking window.
            </div>
          )}
        </div>
        <div style={{ marginBottom: 18, display: "flex", gap: 24 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="radio"
                  name="time"
                  value="am"
                  checked={time === "am"}
                  onChange={() => setTime("am")}
                  disabled={(() => {
                    if (!date) return false;
                    const now = new Date();
                    if (toCalendarDateKey(date) === todayKey && now.getHours() >= 12) return true;
                    if (amCount >= 30) return true;
                    return false;
                  })()}
                />
                <span>AM 9:00-12:00</span>
              </label>
              <div style={{ fontSize: 12, color: '#333', marginTop: 10, fontWeight: 700, marginLeft: 30  }}>
                {loadingCounts ? '0/30' : `${amCount}/30`}
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="radio"
                  name="time"
                  value="pm"
                  checked={time === "pm"}
                  onChange={() => setTime("pm")}
                  disabled={isFriday || (() => {
                    if (!date) return false;
                    const now = new Date();
                    if (toCalendarDateKey(date) === todayKey &&
                      (now.getHours() > 16 || (now.getHours() === 16 && now.getMinutes() >= 30))) return true;
                    if (pmCount >= 30) return true;
                    return false;
                  })()}
                />
                <span>PM 1:30-4:30 {isFriday ? <span style={{ fontSize: 12, color: '#777' }}> (Not available on Fridays)</span> : null}</span>
              </label>
              <div style={{ fontSize: 12, color: '#333', marginTop: 10, fontWeight: 700, marginLeft: 30 }}>
                {loadingCounts ? '0/30' : `${pmCount}/30`}
              </div>
            </div>
          </div>
        {error && <div style={{ color: "red", marginBottom: 10 }}>{error}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 18, marginTop: 18 }}>
          <button type="button" onClick={() => onClose()} style={{ background: "#f5f5f5", color: "#e67e22", border: "none", borderRadius: 6, padding: "8px 22px", fontWeight: 500, fontSize: 15 }}>CLOSE</button>
          <button
            type="submit"
            disabled={submitting || noAppointmentSlots || ((time === 'am' && amCount >= 30) || (time === 'pm' && pmCount >= 30))}
            style={{ background: (submitting || noAppointmentSlots) ? "#ccc" : "#27ae60", color: "#fff", border: "none", borderRadius: 6, padding: "8px 22px", fontWeight: 500, fontSize: 15 }}
            title={noAppointmentSlots ? "No appointment slots available in the configured window." : undefined}
          >{submitting ? "SUBMITTING..." : "SUBMIT"}</button>
        </div>
      </form>
    </div>
  );
}
